import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Info,
  Calendar,
  Backpack,
  Users,
  Phone,
  Dumbbell,
  Map,
  GalleryHorizontal,
  UserPlus,
  Home,
} from "lucide-react";

const Hero = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const { scrollY } = useScroll();
  const sideNavOpacity = useTransform(scrollY, [100, 300], [0, 1]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const linkVariants = {
    hover: {
      scale: 1.05,
      backgroundColor: "#F97316",
      boxShadow: "0 8px 20px rgba(249, 115, 22, 0.5)",
      transition: { duration: 0.3, ease: "easeOut" },
    },
    tap: { scale: 0.95 },
  };

  const logoVariants = {
    initial: { scale: 1.2, opacity: 0 },
    animate: {
      scale: 1,
      opacity: 1,
      transition: { duration: 1.5, ease: "easeOut" },
    },
  };

  const waveVariants = {
    initial: { pathLength: 0, opacity: 0 },
    animate: {
      pathLength: 1,
      opacity: 0.3,
      transition: { duration: 2, ease: "easeInOut" },
    },
  };

  const stickerVariants = {
    initial: { scale: 0, opacity: 0, rotate: -10 },
    animate: {
      scale: 1,
      opacity: 1,
      rotate: 5,
      transition: { duration: 1, delay: 0.5, ease: "easeOut" },
    },
    hover: { scale: 1.1, rotate: 10, transition: { duration: 0.3 } },
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section
      id="hero"
      className="relative h-screen w-full flex items-center justify-center bg-gradient-to-br from-cream to-white overflow-x-hidden overflow-y-hidden"
    >
      {/* Background Waves */}
      <motion.svg
        className="absolute inset-0 z-0 pointer-events-none w-full h-full"
        viewBox="0 0 1920 1080"
        preserveAspectRatio="xMidYMid slice"
        initial="initial"
        animate="animate"
      >
        <motion.path
          d="M 960 1080 C 820 900, 420 600, 0 540"
          fill="none"
          stroke="#F97316"
          strokeWidth="2"
          variants={waveVariants}
        />
        <motion.path
          d="M 960 1080 C 920 950, 520 750, 0 540"
          fill="none"
          stroke="#F97316"
          strokeWidth="2"
          variants={waveVariants}
          transition={{ delay: 0.5 }}
        />
        <motion.path
          style={{ fontFamily: "Baloo 2", fontWeight: 600 }}
          d="M 960 0 C 1100 180, 1500 480, 1920 540"
          fill="none"
          stroke="#F97316"
          strokeWidth="2"
          variants={waveVariants}
        />
        <motion.path
          d="M 960 0 C 1000 130, 1400 330, 1920 540"
          fill="none"
          stroke="#F97316"
          strokeWidth="2"
          variants={waveVariants}
          transition={{ delay: 0.5 }}
        />
      </motion.svg>

      {/* Navbar */}
      <motion.div
        className="fixed bottom-0 left-0 right-0 z-50 flex flex-row justify-around items-center bg-navy/95 backdrop-blur-md shadow-lg border-t-2 border-primary py-2 px-1 sm:flex-col sm:left-0 sm:top-1/2 sm:-translate-y-1/2 sm:border-r-2 sm:border-t-0 sm:rounded-r-2xl sm:py-4 sm:px-3 sm:bottom-auto sm:right-auto sm:justify-start sm:space-y-3 md:left-0 md:top-[50%] md:transform md:-translate-y-1/2 md:flex-col md:items-center md:space-y-4 md:bg-navy/95 md:backdrop-blur-md md:shadow-lg md:border-r-2 md:border-primary md:rounded-r-2xl md:py-6 md:px-4"
        style={{ opacity: sideNavOpacity }}
      >
        <motion.div
          className="relative w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 flex items-center justify-center cursor-pointer rounded-full bg-cream shadow-md border border-primary transform rotate-2 touch-action-manipulation"
          whileHover={{
            scale: 1.1,
            backgroundColor: "#F97316",
            borderColor: "#FFF",
            rotate: 0,
          }}
          whileTap={{ scale: 0.95 }}
          onClick={() => scrollToSection("hero")}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <img
            src="./logo.png"
            alt=""
            className="h-5 w-8 sm:h-6 sm:w-10 md:h-8 md:w-12"
          />
          <motion.div
            className="absolute left-12 sm:left-14 md:left-20 top-1/2 transform -translate-y-1/2 bg-navy text-cream text-xs sm:text-xs md:text-sm font-medium px-2 sm:px-2 md:px-3 py-1 rounded-lg opacity-0 pointer-events-none"
            whileHover={{ opacity: 1 }}
            transition={{ duration: 0.2 }}
          >
            Domov
          </motion.div>
        </motion.div>

        <motion.div
          className="relative w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 flex items-center justify-center cursor-pointer rounded-full bg-cream shadow-md border border-primary transform rotate-2 touch-action-manipulation"
          whileHover={{
            scale: 1.1,
            backgroundColor: "#F97316",
            borderColor: "#FFF",
            rotate: 0,
          }}
          whileTap={{ scale: 0.95 }}
          onClick={() => scrollToSection("info-contact")}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Info
            size={20}
            className="sm:w-7 sm:h-7 md:w-9 md:h-9"
            color="currentColor"
          />
          <motion.div
            className="absolute left-12 sm:left-14 md:left-20 top-1/2 transform -translate-y-1/2 bg-navy text-cream text-xs sm:text-xs md:text-sm font-medium px-2 sm:px-2 md:px-3 py-1 rounded-lg opacity-0 pointer-events-none"
            whileHover={{ opacity: 1 }}
            transition={{ duration: 0.2 }}
          >
            Informácie
          </motion.div>
        </motion.div>

        <div className="flex flex-row space-x-1 sm:space-x-2 sm:flex-col sm:space-x-0 sm:space-y-2 md:flex-col md:items-center md:space-y-3 ">
          {[
            "turnusy",
            "co-si-pribalit",
            "organizator",
            "kontakt",
            "aktivity",
            "kde-nas-najdes",
          ].map((section, index) => (
            <motion.div
              key={section}
              className="relative w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 flex items-center justify-center cursor-pointer rounded-full bg-cream/70 shadow-sm border border-primary transform touch-action-manipulation"
              style={{ rotate: index % 2 === 0 ? 3 : -3 }}
              whileHover={{
                scale: 1.1,
                backgroundColor: "#F97316",
                borderColor: "#FFF",
                rotate: 0,
              }}
              whileTap={{ scale: 0.95 }}
              onClick={() => scrollToSection(section)}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              {section === "turnusy" && (
                <Calendar
                  size={16}
                  className="sm:w-6 sm:h-6 md:w-7 md:h-7"
                  color="currentColor"
                />
              )}
              {section === "co-si-pribalit" && (
                <Backpack
                  size={16}
                  className="sm:w-6 sm:h-6 md:w-7 md:h-7"
                  color="currentColor"
                />
              )}
              {section === "organizator" && (
                <Users
                  size={16}
                  className="sm:w-6 sm:h-6 md:w-7 md:h-7"
                  color="currentColor"
                />
              )}
              {section === "kontakt" && (
                <Phone
                  size={16}
                  className="sm:w-6 sm:h-6 md:w-7 md:h-7"
                  color="currentColor"
                />
              )}
              {section === "aktivity" && (
                <Dumbbell
                  size={16}
                  className="sm:w-6 sm:h-6 md:w-7 md:h-7"
                  color="currentColor"
                />
              )}
              {section === "kde-nas-najdes" && (
                <Map
                  size={16}
                  className="sm:w-6 sm:h-6 md:w-7 md:h-7"
                  color="currentColor"
                />
              )}
              <motion.div
                className="absolute left-10 sm:left-12 md:left-14 top-1/2 transform -translate-y-1/2 bg-navy text-cream text-[10px] sm:text-xs md:text-xs font-medium px-1 sm:px-2 py-0.5 sm:py-1 rounded-lg opacity-0 pointer-events-none"
                whileHover={{ opacity: 1 }}
                transition={{ duration: 0.2 }}
              >
                {section === "turnusy"
                  ? "Turnusy"
                  : section === "co-si-pribalit"
                  ? "Čo si pribaliť"
                  : section === "organizator"
                  ? "Organizátor"
                  : section === "kontakt"
                  ? "Kontakt"
                  : section === "aktivity"
                  ? "Aktivity"
                  : "Kde nás nájdeš"}
              </motion.div>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="relative w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 flex items-center justify-center cursor-pointer rounded-full bg-cream shadow-md border border-primary transform -rotate-2 touch-action-manipulation"
          whileHover={{
            scale: 1.1,
            backgroundColor: "#F97316",
            borderColor: "#FFF",
            rotate: 0,
          }}
          whileTap={{ scale: 0.95 }}
          onClick={() => scrollToSection("gallery")}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <GalleryHorizontal
            size={20}
            className="sm:w-7 sm:h-7 md:w-9 md:h-9"
            color="currentColor"
          />
          <motion.div
            className="absolute left-12 sm:left-14 md:left-20 top-1/2 transform -translate-y-1/2 bg-navy text-cream text-xs sm:text-xs md:text-sm font-medium px-2 sm:px-2 md:px-3 py-1 rounded-lg opacity-0 pointer-events-none"
            whileHover={{ opacity: 1 }}
            transition={{ duration: 0.2 }}
          >
            Galéria
          </motion.div>
        </motion.div>

        <motion.div
          className="relative w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 flex items-center justify-center cursor-pointer rounded-full bg-cream shadow-md border border-primary transform rotate-2 touch-action-manipulation"
          whileHover={{
            scale: 1.1,
            backgroundColor: "#F97316",
            borderColor: "#FFF",
            rotate: 0,
          }}
          whileTap={{ scale: 0.95 }}
          onClick={() => scrollToSection("registration")}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <UserPlus
            size={20}
            className="sm:w-7 sm:h-7 md:w-9 md:h-9"
            color="currentColor"
          />
          <motion.div
            className="absolute left-12 sm:left-14 md:left-20 top-1/2 transform -translate-y-1/2 bg-navy text-cream text-xs sm:text-xs md:text-sm font-medium px-2 sm:px-2 md:px-3 py-1 rounded-lg opacity-0 pointer-events-none"
            whileHover={{ opacity: 1 }}
            transition={{ duration: 0.2 }}
          >
            Prihlásiť sa
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Main Content */}
      <div className="relative z-10 w-full h-full flex items-center justify-center">
        {/* Desktop Layout (≥ 768px) */}
        <div className="hidden md:flex items-center justify-center w-full h-full relative">
          <motion.div
            className="absolute left-10 top-[30%] transform -translate-y-1/2 max-w-[280px] md:max-w-[300px] lg:max-w-[320px] xl:max-w-[340px] 2xl:left-20 2xl:max-w-md 2xl:top-[29%]"
            initial={{ opacity: 0, x: -100 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
          >
            <motion.h1
              className="text-5xl md:text-6xl lg:text-7xl xl:text-7xl 2xl:text-7xl 3xl:text-8xl font-bold text-navy uppercase tracking-tight drop-shadow-md transform rotate-1"
              style={{ fontFamily: "Baloo 2", fontWeight: 800 }}
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            >
              Ahojte Kamaráti!
            </motion.h1>
            <motion.p
              className="text-2xl md:text-2xl lg:text-3xl xl:text-3xl 2xl:text-4xl text-primary mt-3 md:mt-3 lg:mt-4 xl:mt-4 2xl:mt-6 leading-tight transform -rotate-1 2xl:-rotate-2"
              style={{ fontFamily: "Baloo 2", fontWeight: 600 }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.8 }}
            >
              Tábor plný zábavy
              <br />a dobrodružstva!
            </motion.p>
            <motion.p
              className="text-lg md:text-xl lg:text-xl xl:text-2xl 2xl:text-2xl text-navy mt-6 md:mt-6 lg:mt-8 xl:mt-8 2xl:mt-6 transform rotate-1"
              style={{ fontFamily: "Baloo 2", fontWeight: 400 }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.8 }}
            >
              Hry, priatelia a nezabudnuteľné zážitky!
            </motion.p>
          </motion.div>

          <motion.div
            className="relative flex flex-col items-center -top-10 md:-top-12 lg:-top-14 xl:-top-16 2xl:-top-16"
            variants={logoVariants}
            initial="initial"
            animate="animate"
          >
            <div className="relative w-[450px] h-[450px] md:w-[450px] md:h-[450px] lg:w-[500px] lg:h-[500px] xl:w-[550px] xl:h-[550px] 2xl:w-[700px] 2xl:h-[500px] rounded-2xl p-6 md:p-6 lg:p-8 xl:p-8 2xl:p-10 transform rotate-1">
              <img
                src="./logo.png"
                alt="Diecezny Tabor Logo"
                className="w-full h-full object-contain"
              />
            </div>
            <motion.div
              className="mt-3 md:mt-3 lg:mt-4 xl:mt-4 2xl:mt-10 text-center relative xl:-mb-10 lg:-mb-20 2xl:mb-0"
              variants={logoVariants}
              initial="initial"
              animate="animate"
            >
              <h2
                className="text-6xl md:text-7xl lg:text-8xl xl:text-8xl 2xl:text-7xl 3xl:text-9xl font-bold text-navy uppercase transform -rotate-1 2xl:-rotate-2 -mt-8 md:-mt-8 lg:-mt-10 xl:-mt-8 2xl:-mt-12"
                style={{ fontFamily: "Baloo 2", fontWeight: 800 }}
              >
                DIECÉZNY TÁBOR
              </h2>
              <div className="relative flex items-center justify-center mt-1 md:mt-1 lg:mt-2 xl:mt-2 2xl:mt-2">
                <span
                  className="text-3xl md:text-4xl lg:text-5xl xl:text-5xl 2xl:text-6xl text-primary font-semibold border-2 border-primary rounded-lg px-3 md:px-3 lg:px-4 xl:px-4 2xl:px-4 py-1 md:py-1 lg:py-1 xl:py-1 2xl:py-1 inline-block transform rotate-1 2xl:rotate-2"
                  style={{ fontFamily: "Baloo 2", fontWeight: 600 }}
                >
                  Žilinskej diecézy
                  <motion.img
                    src="/Erb_zilinska_dieceza.png"
                    alt="Žilinská Diecéza Emblem"
                    className="absolute -right-12  md:-right-10 lg:-right-24 xl:-right-40 2xl:-right-40 -top-10 transform -translate-y-1/2 w-24 md:w-14 lg:w-24 xl:w-32 2xl:w-40 h-24 sm:h-24 sm:w-24 md:h-14 lg:h-24 xl:h-32 2xl:h-40 drop-shadow-md z-[1001]"
                    variants={stickerVariants}
                    initial="initial"
                    animate="animate"
                    whileHover="hover"
                  />
                </span>
              </div>
            </motion.div>
          </motion.div>

          <motion.div
            className="absolute right-10 top-[30%] transform -translate-y-1/2 text-right max-w-[280px] md:max-w-[300px] lg:max-w-[320px] xl:max-w-[340px] 2xl:right-20 2xl:max-w-lg 2xl:top-[28%]"
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
          >
            <div className="flex flex-col space-y-4 md:space-y-5 lg:space-y-6 xl:space-y-6 2xl:space-y-8">
              {["info-contact", "gallery"].map((section) => (
                <motion.div
                  key={section}
                  variants={linkVariants}
                  className="relative"
                >
                  <Link
                    to={`/#${section}`}
                    className="text-xl md:text-2xl lg:text-3xl xl:text-3xl 2xl:text-4xl font-bold uppercase tracking-wide px-6 md:px-7 lg:px-8 xl:px-8 2xl:px-10 py-3 md:py-3 lg:py-4 xl:py-4 2xl:py-5 rounded-xl bg-primary text-cream shadow-md border-2 border-cream flex items-center justify-center relative overflow-hidden transform rotate-1 2xl:rotate-2"
                    onClick={() => scrollToSection(section)}
                  >
                    {section === "info-contact" ? "Informácie" : "Galéria"}
                    <motion.div
                      className="absolute inset-0 bg-orange-500 opacity-0"
                      transition={{ duration: 0.3 }}
                    />
                  </Link>
                </motion.div>
              ))}
            </div>
            <motion.div
              className="mt-6 md:mt-6 lg:mt-8 xl:mt-8 2xl:mt-10 flex justify-end space-x-4 md:space-x-4 lg:space-x-5 xl:space-x-5 2xl:space-x-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.8 }}
            >
              <motion.div
                className="relative bg-cream text-navy px-4 md:px-4 lg:px-5 xl:px-5 2xl:px-6 py-2 md:py-2 lg:py-2 xl:py-2 2xl:py-3 rounded-lg shadow-md border border-primary transform -rotate-2 2xl:-rotate-3"
                whileHover={{
                  scale: 1.05,
                  backgroundColor: "#F97316",
                  color: "#FFF",
                  rotate: 0,
                }}
                transition={{ duration: 0.3 }}
              >
                <span
                  className="text-lg md:text-lg lg:text-xl xl:text-xl 2xl:text-2xl font-bold"
                  style={{ fontFamily: "Baloo 2" }}
                >
                  8 — 11 rokov
                </span>
                <motion.div
                  className="absolute -top-2 -right-2 w-3 md:w-3 lg:w-4 xl:w-4 2xl:w-4 h-3 md:h-3 lg:h-4 xl:h-4 2xl:h-4 bg-primary rounded-full"
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />
              </motion.div>
              <motion.div
                className="relative bg-cream text-navy px-4 md:px-4 lg:px-5 xl:px-5 2xl:px-6 py-2 md:py-2 lg:py-2 xl:py-2 2xl:py-3 rounded-lg shadow-md border border-primary transform -rotate-2 2xl:-rotate-3"
                whileHover={{
                  scale: 1.05,
                  backgroundColor: "#F97316",
                  color: "#FFF",
                  rotate: 0,
                }}
                transition={{ duration: 0.3 }}
              >
                <span
                  className="text-lg md:text-lg lg:text-xl xl:text-xl 2xl:text-2xl font-bold"
                  style={{ fontFamily: "Baloo 2" }}
                >
                  12 — 14 rokov
                </span>
                <motion.div
                  className="absolute -top-2 -right-2 w-3 md:w-3 lg:w-4 xl:w-4 2xl:w-4 h-3 md:h-3 lg:h-4 xl:h-4 2xl:h-4 bg-primary rounded-full"
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />
              </motion.div>
            </motion.div>
            <motion.p
              className="text-xl md:text-2xl lg:text-2xl xl:text-3xl 2xl:text-3xl text-primary mt-6 md:mt-6 lg:mt-8 xl:mt-8 2xl:mt-8 font-bold drop-shadow-md transform -rotate-1 2xl:-rotate-2"
              style={{ fontFamily: "Baloo 2", fontWeight: 700 }}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.7, duration: 0.8 }}
            >
              Pridaj sa k nám!
            </motion.p>
            <motion.div
              className="mt-6 md:mt-6 lg:mt-8 xl:mt-8 2xl:mt-8"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.8 }}
            >
              <Link
                to="/#registration"
                className="inline-block bg-orange-500 text-cream px-8 md:px-8 lg:px-10 xl:px-10 2xl:px-12 py-3 md:py-3 lg:py-4 xl:py-4 2xl:py-5 rounded-full uppercase font-bold shadow-lg text-lg md:text-lg lg:text-xl xl:text-xl 2xl:text-2xl relative overflow-hidden group transform rotate-1 2xl:rotate-2"
                onClick={() => scrollToSection("registration")}
              >
                <motion.span
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                  initial={{ x: "-100%" }}
                  whileHover={{ x: "100%" }}
                  transition={{ duration: 0.5, ease: "easeInOut" }}
                />
                Prihlásiť sa
                <motion.span
                  className="absolute -bottom-2 -right-2 w-3 md:w-3 lg:w-4 xl:w-4 2xl:w-4 h-3 md:h-3 lg:h-4 xl:h-4 2xl:h-4 bg-cream rounded-full"
                  whileHover={{ scale: 1.5 }}
                  transition={{ duration: 0.3 }}
                />
              </Link>
            </motion.div>
          </motion.div>
        </div>

        {/* Tablet and Mobile Layout (< 768px) */}
        <div className="md:hidden flex flex-col items-center justify-center w-full h-full px-4 sm:px-6">
          {/* Logo Section */}
          <motion.div
            className="mb-2 sm:mb-4"
            variants={logoVariants}
            initial="initial"
            animate="animate"
          >
            <div className="relative w-[60vw] h-[20vh] sm:w-[40vw] sm:h-[30vh] rounded-2xl p-2 sm:p-4 transform rotate-1 max-w-full">
              <img
                src="./logo.png"
                alt="Diecezny Tabor Logo"
                className="w-full h-full object-contain"
              />
            </div>
            <motion.div
              className="mt-1 sm:mt-2 text-center relative"
              variants={logoVariants}
              initial="initial"
              animate="animate"
            >
              <h2
                className="text-3xl sm:text-4xl font-bold text-navy uppercase transform -rotate-2 -mt-1 sm:-mt-2"
                style={{ fontFamily: "Baloo 2", fontWeight: 800 }}
              >
                DIECÉZNY TÁBOR
              </h2>
              <div className="relative flex items-center justify-center mt-0.5 sm:mt-1">
                <span
                  className="text-lg sm:text-xl text-primary font-semibold border-2 border-primary rounded-lg px-1 sm:px-2 py-0.5 sm:py-1 inline-block transform rotate-2"
                  style={{ fontFamily: "Baloo 2", fontWeight: 600 }}
                >
                  Žilinskej Diecézy
                </span>
                <motion.img
                  src="/Erb_zilinska_dieceza.png"
                  alt="Žilinská Diecéza Emblem"
                  className="absolute -right-10 sm:-right-12 top-1/2 transform -translate-y-1/2 w-8 sm:w-10 h-8 sm:h-10 drop-shadow-md z-[1001]"
                  variants={stickerVariants}
                  initial="initial"
                  animate="animate"
                  whileHover="hover"
                />
              </div>
            </motion.div>
          </motion.div>

          {/* Text Section */}
          <motion.div
            className="text-center w-full px-2 sm:px-4"
            initial={{ opacity: 0, x: -100 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
          >
            <motion.h1
              className="text-3xl sm:text-4xl font-bold text-navy uppercase tracking-tight drop-shadow-md transform rotate-1"
              style={{ fontFamily: "Baloo 2", fontWeight: 800 }}
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            >
              Ahojte Kamaráti!
            </motion.h1>
            <motion.p
              className="text-lg sm:text-xl text-primary mt-1 sm:mt-2 leading-tight transform -rotate-2"
              style={{ fontFamily: "Baloo 2", fontWeight: 600 }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.8 }}
            >
              Tábor plný zábavy a dobrodružstva!
            </motion.p>
            <motion.p
              className="text-sm sm:text-base text-navy mt-2 sm:mt-3 transform rotate-1"
              style={{ fontFamily: "Baloo 2", fontWeight: 400 }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.8 }}
            >
              Hry, priatelia a nezabudnuteľné zážitky čakajú na teba!
            </motion.p>
          </motion.div>

          {/* Buttons and Age Groups */}
          <motion.div
            className="text-center w-full mt-2 sm:mt-3"
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
          >
            <div className="flex flex-col space-y-2 sm:space-y-3">
              {["info-contact", "gallery"].map((section) => (
                <motion.div
                  key={section}
                  variants={linkVariants}
                  className="relative"
                >
                  <Link
                    to={`/#${section}`}
                    className="text-base sm:text-lg font-bold uppercase tracking-wide px-4 sm:px-5 py-2 sm:py-2.5 rounded-xl bg-primary text-cream shadow-md border-2 border-cream flex items-center justify-center relative overflow-hidden transform rotate-2 w-full max-w-[200px] sm:max-w-[250px] mx-auto"
                    onClick={() => scrollToSection(section)}
                  >
                    {section === "info-contact" ? "Informácie" : "Galéria"}
                    <motion.div
                      className="absolute inset-0 bg-orange-500 opacity-0"
                      transition={{ duration: 0.3 }}
                    />
                  </Link>
                </motion.div>
              ))}
            </div>
            <motion.div
              className="mt-2 sm:mt-3 flex flex-col sm:flex-row justify-center space-y-2 sm:space-y-0 sm:space-x-3"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.8 }}
            >
              <motion.div
                className="relative bg-cream text-navy px-3 sm:px-4 py-1 sm:py-1.5 rounded-lg shadow-md border border-primary transform -rotate-3"
                whileHover={{
                  scale: 1.05,
                  backgroundColor: "#F97316",
                  color: "#FFF",
                  rotate: 0,
                }}
                transition={{ duration: 0.3 }}
              >
                <span
                  className="text-sm sm:text-base font-bold"
                  style={{ fontFamily: "Baloo 2" }}
                >
                  8 — 11 rokov
                </span>
                <motion.div
                  className="absolute -top-1 sm:-top-2 -right-1 sm:-right-2 w-2 sm:w-3 h-2 sm:h-3 bg-primary rounded-full"
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />
              </motion.div>
              <motion.div
                className="relative bg-cream text-navy px-3 sm:px-4 py-1 sm:py-1.5 rounded-lg shadow-md border border-primary transform rotate-3"
                whileHover={{
                  scale: 1.05,
                  backgroundColor: "#F97316",
                  color: "#FFF",
                  rotate: 0,
                }}
                transition={{ duration: 0.3 }}
              >
                <span
                  className="text-sm sm:text-base font-bold"
                  style={{ fontFamily: "Baloo 2" }}
                >
                  12 — 14 rokov—
                </span>
                <motion.div
                  className="absolute -top-1 sm:-top-2 -right-1 sm:-right-2 w-2 sm:w-3 h-2 sm:h-3 bg-primary rounded-full"
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />
              </motion.div>
            </motion.div>
            <motion.p
              className="text-base sm:text-lg text-primary mt-2 sm:mt-3 font-bold drop-shadow-md transform -rotate-2"
              style={{ fontFamily: "Baloo 2", fontWeight: 700 }}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.7, duration: 0.8 }}
            >
              Pridaj sa k nám!
            </motion.p>
            <motion.div
              className="mt-2 sm:mt-3"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.8 }}
            >
              <Link
                to="/#registration"
                className="inline-block bg-orange-500 text-cream px-6 sm:px-8 py-2 sm:py-2.5 rounded-full uppercase font-bold shadow-lg text-sm sm:text-base relative overflow-hidden group transform rotate-2 w-full max-w-[180px] sm:max-w-[200px] mx-auto"
                onClick={() => scrollToSection("registration")}
              >
                <motion.span
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                  initial={{ x: "-100%" }}
                  whileHover={{ x: "100%" }}
                  transition={{ duration: 0.5, ease: "easeInOut" }}
                />
                Prihlásiť sa
                <motion.span
                  className="absolute -bottom-1 sm:-bottom-2 -right-1 sm:-right-2 w-2 sm:w-3 h-2 sm:h-3 bg-cream rounded-full"
                  whileHover={{ scale: 1.5 }}
                  transition={{ duration: 0.3 }}
                />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Decorative Images (Visible only on Desktop) */}
      <motion.img
        src="./assets/voleyball.svg"
        alt="Soccer Ball"
        className="absolute bottom-12 md:bottom-12 lg:bottom-2 xl:bottom-16 2xl:bottom-6 left-16 md:left-16 lg:left-20 xl:left-24 2xl:left-32 h-16 md:h-16 lg:h-18 xl:h-18 2xl:h-20 drop-shadow-md transform rotate-3 2xl:rotate-5 hidden md:block"
        animate={{ y: [0, -15, 0], rotate: [3, 10, -3, 3] }}
        transition={{
          duration: 3,
          repeat: Infinity,
          ease: "easeInOut",
          times: [0, 0.4, 0.6, 1],
        }}
      />
      <motion.img
        src="./assets/gitara.svg"
        alt="Guitar"
        className="absolute top-12 md:top-12 lg:top-14 xl:top-14 2xl:top-16 right-32 md:right-32 lg:right-56 xl:right-56 2xl:right-60 h-16 md:h-16 lg:h-18 xl:h-18 2xl:h-20 drop-shadow-md transform -rotate-3 2xl:-rotate-5 hidden md:block"
        animate={{ x: [0, 6, -6, 0], rotate: [-3, 0, -8, -3] }}
        transition={{
          duration: 3.5,
          repeat: Infinity,
          ease: "easeInOut",
          times: [0, 0.3, 0.7, 1],
        }}
      />
      <motion.img
        src="./assets/paleta.svg"
        alt="Paint Palette"
        className="absolute top-20 md:top-20 lg:top-24 xl:top-24 2xl:top-28 left-16 md:left-16 lg:left-20 xl:left-24 2xl:left-32 h-16 md:h-16 lg:h-18 xl:h-18 2xl:h-20 drop-shadow-md transform rotate-2 2xl:rotate-3 hidden md:block"
        animate={{
          y: [0, 10, 0],
          rotate: [2, 8, -2, 2],
          scale: [1, 1.05, 1],
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut",
          times: [0, 0.4, 0.6, 1],
        }}
      />
      <motion.img
        src="./assets/ruksak.svg"
        alt="Backpack"
        className="absolute top-20 md:top-20 lg:top-24 xl:top-24 2xl:top-28 right-10 md:right-16 lg:right-20 xl:right-24 2xl:right-32 h-16 md:h-16 lg:h-18 xl:h-18 2xl:h-20 drop-shadow-md transform -rotate-2 2xl:-rotate-3 hidden md:block"
        animate={{ y: [0, -10, 0], rotate: [-2, 2, -6, -2] }}
        transition={{
          duration: 3.5,
          repeat: Infinity,
          ease: "easeInOut",
          times: [0, 0.5, 0.7, 1],
        }}
      />
    </section>
  );
};

export default Hero;
